import React, { useState, useEffect } from 'react';
import { updateProgramCapacity, getProgramCapacityStatus, getProgramMembers } from '../../../../api/programs';
import { useNotification } from '../../../../context/NotificationContext';
import styles from './CapacityManager.module.css';

/**
 * CapacityManager - Phase 3H
 * 
 * Program capacity settings with:
 * - Current status display
 * - Max/min participants
 * - Waitlist configuration
 * - Auto-enroll settings
 */
const CapacityManager = ({ programId, onUpdate }) => {
  const { showSuccess, showError } = useNotification();
  
  const [capacity, setCapacity] = useState({
    maxParticipants: '',
    minParticipants: '',
    waitlistEnabled: false,
    waitlistLimit: '',
    autoEnrollFromWaitlist: true
  });
  
  const [status, setStatus] = useState(null);
  const [currentEnrolled, setCurrentEnrolled] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch current status
  useEffect(() => {
    const fetchStatus = async () => {
      if (!programId) return;
      
      try {
        setLoading(true);
        
        const statusData = await getProgramCapacityStatus(programId);
        setStatus(statusData);
        
        try {
          const enrolled = await getProgramMembers(programId, { status: 'Enrolled' });
          setCurrentEnrolled(Array.isArray(enrolled) ? enrolled.length : 0);
        } catch {
          setCurrentEnrolled(statusData.currentEnrolled || 0);
        }
        
        setCapacity({
          maxParticipants: statusData.maxParticipants || '',
          minParticipants: statusData.minParticipants || '',
          waitlistEnabled: statusData.waitlistEnabled || false,
          waitlistLimit: statusData.waitlistLimit || '',
          autoEnrollFromWaitlist: statusData.autoEnrollFromWaitlist !== false
        });
      } catch (err) {
        console.error('Error fetching capacity:', err);
        showError('Failed to load capacity settings');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [programId]);

  // Handle change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCapacity(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);

      const maxParticipants = capacity.maxParticipants ? Number(capacity.maxParticipants) : null;
      const minParticipants = capacity.minParticipants ? Number(capacity.minParticipants) : 1;
      const waitlistLimit = capacity.waitlistLimit ? Number(capacity.waitlistLimit) : null;

      // Validation
      if (maxParticipants && minParticipants && minParticipants > maxParticipants) {
        showError('Minimum participants cannot exceed maximum');
        return;
      }

      if (capacity.waitlistLimit && !capacity.waitlistEnabled) {
        showError('Waitlist limit requires waitlist to be enabled');
        return;
      }

      const settings = {
        maxParticipants,
        minParticipants,
        waitlistEnabled: capacity.waitlistEnabled,
        waitlistLimit,
        autoEnrollFromWaitlist: capacity.autoEnrollFromWaitlist
      };

      await updateProgramCapacity(programId, settings);
      
      const updatedStatus = await getProgramCapacityStatus(programId);
      setStatus(updatedStatus);
      
      showSuccess('Capacity settings updated');
      onUpdate?.(updatedStatus);
    } catch (err) {
      showError(err.message || 'Failed to update capacity');
    } finally {
      setSaving(false);
    }
  };

  // Calculate capacity percentage
  const getCapacityPercentage = () => {
    if (!status?.maxParticipants) return null;
    return Math.min(100, (currentEnrolled / status.maxParticipants) * 100);
  };

  const capacityPercent = getCapacityPercentage();

  if (loading) {
    return (
      <div className={styles.capacityManager}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading capacity settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.capacityManager}>
      {/* Header with Status */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>👥 Capacity Settings</h2>
          <p>Configure program capacity and waitlist</p>
        </div>
        
        <div className={styles.currentStatus}>
          <div className={styles.statusItem}>
            <span className={styles.statusValue}>{currentEnrolled}</span>
            <span className={styles.statusLabel}>Enrolled</span>
          </div>
          {status?.maxParticipants && (
            <>
              <span className={styles.statusDivider}>/</span>
              <div className={styles.statusItem}>
                <span className={styles.statusValue}>{status.maxParticipants}</span>
                <span className={styles.statusLabel}>Max</span>
              </div>
            </>
          )}
          {status?.waitlistCount > 0 && (
            <div className={styles.waitlistBadge}>
              {status.waitlistCount} on waitlist
            </div>
          )}
        </div>
      </div>

      {/* Capacity Bar */}
      {capacityPercent !== null && (
        <div className={styles.capacityBar}>
          <div className={styles.barTrack}>
            <div 
              className={`${styles.barFill} ${capacityPercent >= 90 ? styles.critical : capacityPercent >= 70 ? styles.warning : ''}`}
              style={{ width: `${capacityPercent}%` }}
            ></div>
          </div>
          <div className={styles.barLabels}>
            <span>{capacityPercent.toFixed(0)}% capacity used</span>
            <span>{status.maxParticipants - currentEnrolled} spots remaining</span>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formSection}>
          <h3>Participant Limits</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="maxParticipants">Maximum Participants</label>
              <input
                type="number"
                id="maxParticipants"
                name="maxParticipants"
                value={capacity.maxParticipants}
                onChange={handleChange}
                min="1"
                placeholder="Unlimited"
              />
              <span className={styles.hint}>Leave empty for unlimited</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="minParticipants">Minimum Participants</label>
              <input
                type="number"
                id="minParticipants"
                name="minParticipants"
                value={capacity.minParticipants}
                onChange={handleChange}
                min="1"
                placeholder="1"
              />
              <span className={styles.hint}>Minimum to run program</span>
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Waitlist Settings</h3>
          
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="waitlistEnabled"
              checked={capacity.waitlistEnabled}
              onChange={handleChange}
            />
            <span className={styles.checkboxCustom}></span>
            <div className={styles.checkboxText}>
              <span>Enable Waitlist</span>
              <span className={styles.checkboxHint}>Allow participants to join waitlist when full</span>
            </div>
          </label>

          {capacity.waitlistEnabled && (
            <div className={styles.waitlistSettings}>
              <div className={styles.formGroup}>
                <label htmlFor="waitlistLimit">Waitlist Limit</label>
                <input
                  type="number"
                  id="waitlistLimit"
                  name="waitlistLimit"
                  value={capacity.waitlistLimit}
                  onChange={handleChange}
                  min="1"
                  placeholder="Unlimited"
                />
                <span className={styles.hint}>Maximum people on waitlist</span>
              </div>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="autoEnrollFromWaitlist"
                  checked={capacity.autoEnrollFromWaitlist}
                  onChange={handleChange}
                />
                <span className={styles.checkboxCustom}></span>
                <div className={styles.checkboxText}>
                  <span>Auto-enroll from waitlist</span>
                  <span className={styles.checkboxHint}>Automatically enroll when spots open</span>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Warning */}
        {status && capacity.maxParticipants && Number(capacity.maxParticipants) < currentEnrolled && (
          <div className={styles.warning}>
            <span className={styles.warningIcon}>⚠️</span>
            <span>
              Setting max to {capacity.maxParticipants} is below current enrollment of {currentEnrolled}. 
              This may affect program operations.
            </span>
          </div>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <button 
            type="submit" 
            className={styles.saveBtn}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className={styles.savingSpinner}></span>
                Saving...
              </>
            ) : (
              <>💾 Save Settings</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CapacityManager;
